class CustomSearch extends HTMLElement {
  constructor() {
    super();
    this.maxItems = 8;
    this.storageKey = 'recent_searches';
  }

  connectedCallback() {
    this.input = this.querySelector('[data-search-input]');
    this.list = this.querySelector('[data-recent-searches]');
    this.container = this.querySelector('[data-recent-searches-container]');
    this.collections = this.querySelectorAll('[data-search-collection]');
    this.recommendationTags = this.querySelectorAll('[data-search-recommendation]');

    this.render();
    this.setupSaveEvents();
    this.setupCollectionTabs();
    this.setupRecommendationTags();
    this.setupClearAllButton();
    this.setupCloseButton();
    this.checkActiveTag();
    this.setupBodyScrollLock();
    this.setupDragScroll();
    this.setupRecommendedProducts();
  }

  // dataLayer: gnb 카테고리 탭 라벨 반환
  getGnbLabel() {
    var map = { 'all': '전체', '여성': 'Women', '남성': 'Men', '키즈': 'Kids' };
    return map[this.currentCollection] || '전체';
  }

  setupDragScroll() {
    const list = this.querySelector('.recommended-products-list');
    if (!list) return;

    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let hasDragged = false;

    // 이미지 기본 드래그 방지
    list.querySelectorAll('img').forEach(img => {
      img.draggable = false;
    });

    // 링크 드래그 방지
    list.querySelectorAll('a').forEach(link => {
      link.style.webkitUserDrag = 'none';
    });

    if (isTouchDevice) {
      // 터치 디바이스: 터치 이벤트 기반 드래그 스크롤
      list.addEventListener('touchstart', (e) => {
        isDragging = true;
        hasDragged = false;
        startX = e.touches[0].clientX;
        startScrollLeft = list.scrollLeft;
      }, { passive: true });

      list.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        const diff = e.touches[0].clientX - startX;

        if (Math.abs(diff) > 5) {
          if (!hasDragged) {
            hasDragged = true;
            list.classList.add('is-dragging');
          }
          list.scrollLeft = startScrollLeft - diff;
        }
      }, { passive: true });

      list.addEventListener('touchend', () => {
        if (!isDragging) return;
        isDragging = false;

        setTimeout(() => {
          list.classList.remove('is-dragging');
        }, 100);

        hasDragged = false;
      }, { passive: true });

      // 드래그 중 클릭(링크 이동) 방지
      list.addEventListener('click', (e) => {
        if (list.classList.contains('is-dragging')) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    } else {
      // PC: 마우스 이벤트 기반 드래그 스크롤
      const handleMouseDown = (e) => {
        if (e.button !== 0) return;
        isDragging = true;
        hasDragged = false;
        startX = e.clientX;
        startScrollLeft = list.scrollLeft;
        list.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';
      };

      const handleMouseMove = (e) => {
        if (!isDragging) return;
        const diff = e.clientX - startX;

        // 5px 이상 이동해야 드래그로 인식
        if (Math.abs(diff) > 5) {
          if (!hasDragged) {
            hasDragged = true;
            list.classList.add('is-dragging');
          }
          e.preventDefault();
          list.scrollLeft = startScrollLeft - diff;
        }
      };

      const handleMouseUp = () => {
        if (!isDragging) return;
        isDragging = false;
        list.style.cursor = 'grab';
        document.body.style.userSelect = '';

        setTimeout(() => {
          list.classList.remove('is-dragging');
        }, 100);

        hasDragged = false;
      };

      list.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      list.style.cursor = 'grab';

      // 드래그 중 클릭(링크 이동) 방지
      list.addEventListener('click', (e) => {
        if (list.classList.contains('is-dragging')) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    }
  }

  setupBodyScrollLock() {
    const details = this.querySelector('details');
    if (!details) return;

    const isMobile = () => window.innerWidth < 750;
    let scrollPosition = 0;

    details.addEventListener('toggle', () => {
      // 모달이 열릴 때마다 최근 검색어 상태 업데이트
      if (details.open) {
        this.render();
      }

      if (isMobile()) {
        if (details.open) {
          scrollPosition = window.scrollY;
          document.body.style.overflow = 'hidden';
          document.body.style.position = 'fixed';
          document.body.style.top = `-${scrollPosition}px`;
          document.body.style.width = '100%';
          document.documentElement.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = '';
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.width = '';
          document.documentElement.style.overflow = '';
          window.scrollTo(0, scrollPosition);
        }
      }
    });
  }

  setupClearAllButton() {
    const clearAllButton = this.querySelector('[data-clear-all-searches]');
    if (!clearAllButton) return;

    clearAllButton.addEventListener('click', () => {
      this.clearAll();
    });
  }

  clearAll() {
    localStorage.removeItem(this.storageKey);
    this.render();
  }

  setupCloseButton() {
    const closeButton = this.querySelector('[data-search-close]');
    if (!closeButton) return;

    closeButton.addEventListener('click', () => {
      const detailsModal = this.querySelector('details-modal');
      if (detailsModal && typeof detailsModal.close === 'function') {
        detailsModal.close();
      } else {
        const details = this.querySelector('details');
        if (details) {
          details.removeAttribute('open');
        }
      }
    });
  }

  setupSaveEvents() {
    this.input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const term = this.input.value.trim();
        if (term.length > 0) this.save(term);
      }
    });

    const form = this.querySelector('[data-search-form]');
    form?.addEventListener('submit', (e) => {
      let term = this.input.value.trim();
      const originalTerm = term;
      
      // dataLayer: search_keyword (search_keyword / gnb_search)
      if (typeof FilaDL !== 'undefined' && term) {
        var dlType = (this.currentCollection && this.currentCollection !== 'all') ? 'gnb_search' : 'search_keyword';
        FilaDL.push({ event: 'search_keyword', keyword: term, type: dlType, gnb: this.getGnbLabel() });
      }

      if (this.currentCollection && this.currentCollection !== 'all') {
        e.preventDefault();

        if (originalTerm.length > 0) this.save(originalTerm);

        const searchUrl = '/search';
        const tagParam = encodeURIComponent(this.currentCollection);
        let url = `${searchUrl}?filter.p.tag=${tagParam}`;

        if (term) {
          const searchParam = encodeURIComponent(term);
          url += `&q=${searchParam}`;
        } else {
          url += `&q=*`;
        }

        this.showLoading();
        window.location.href = url;
      } else {
        if (term.length > 0) this.save(term);
        this.showLoading();
      }
    });
  }

  setupCollectionTabs() {
    if (this.collections.length === 0) return;

    this.collections[0].classList.add('active');
    this.currentCollection = this.collections[0].dataset.searchCollection;

    this.collections.forEach(col => {
      col.addEventListener('click', () => {
        const clickedCollection = col.dataset.searchCollection;

        // 이미 선택된 탭이면 아무것도 하지 않음
        if (this.currentCollection === clickedCollection) return;

        this.collections.forEach(c => c.classList.remove('active'));
        col.classList.add('active');
        this.currentCollection = clickedCollection;

        // 탭 클릭 시 검색어가 있을 때만 검색 실행
        const term = this.input?.value.trim() || '';
        if (term) {
          this.executeSearch();
        }
      });
    });
  }

  executeSearch() {
    const term = this.input?.value.trim() || '';
    const searchUrl = '/search';
    let url = searchUrl;

    if (this.currentCollection && this.currentCollection !== 'all') {
      const tagParam = encodeURIComponent(this.currentCollection);
      url += `?filter.p.tag=${tagParam}`;

      if (term) {
        url += `&q=${encodeURIComponent(term)}`;
      } else {
        url += `&q=*`;
      }
    } else {
      if (term) {
        url += `?q=${encodeURIComponent(term)}`;
      } else {
        url += `?q=*`;
      }
    }

    if (term) this.save(term);
    this.showLoading();
    window.location.href = url;
  }

  checkActiveTag() {
    const urlParams = new URLSearchParams(window.location.search);
    const activeTag = urlParams.get('filter.p.tag');
    
    if (activeTag && this.collections.length > 0) {
      const decodedTag = decodeURIComponent(activeTag).toLowerCase();
      
      this.collections.forEach(col => {
        const collectionTag = col.dataset.searchCollection?.toLowerCase();
        if (collectionTag === decodedTag) {
          this.collections.forEach(c => c.classList.remove('active'));
          col.classList.add('active');
          this.currentCollection = col.dataset.searchCollection;
        }
      });
    }
  }

  setupRecommendationTags() {
    if (!this.input || this.recommendationTags.length === 0) return;

    this.recommendationTags.forEach(tag => {
      tag.addEventListener('click', () => {
        const term = tag.dataset.searchRecommendation;
        if (!term) return;

        // 검색어 저장
        this.save(term);

        // 검색 URL 생성 및 바로 이동
        const searchUrl = '/search';
        let url;

        if (this.currentCollection && this.currentCollection !== 'all') {
          url = `${searchUrl}?filter.p.tag=${encodeURIComponent(this.currentCollection)}&q=${encodeURIComponent(term)}`;
        } else {
          url = `${searchUrl}?q=${encodeURIComponent(term)}`;
        }

        // dataLayer: search_keyword (recommend_keyword)
        if (typeof FilaDL !== 'undefined') {
          FilaDL.push({ event: 'search_keyword', keyword: term, type: 'recommend_keyword', gnb: this.getGnbLabel() });
        }

        this.showLoading();
        window.location.href = url;
      });
    });
  }

  // dataLayer: search_keyword (recommend_item) — 추천 상품 클릭
  setupRecommendedProducts() {
    var list = this.querySelector('[data-recommended-list]');
    if (!list) return;
    var self = this;

    list.addEventListener('click', function(e) {
      if (list.classList.contains('is-dragging')) return;
      var item = e.target.closest('.recommended-product-item');
      if (!item) return;
      var titleEl = item.querySelector('.recommended-product-title');
      if (typeof FilaDL !== 'undefined') {
        FilaDL.push({
          event: 'search_keyword',
          keyword: titleEl ? titleEl.textContent.trim() : '',
          type: 'recommend_item',
          gnb: self.getGnbLabel()
        });
      }
    });
  }

  showLoading() {
    let overlay = this.querySelector('[data-search-loading]') || document.querySelector('[data-search-loading]');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'search-loading-overlay';
      overlay.setAttribute('data-search-loading', '');
      overlay.innerHTML = '<div class="search-loading-overlay__spinner"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44 44"><circle cx="22" cy="22" r="20" fill="none" stroke="currentColor" stroke-width="3" stroke-dasharray="80" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" from="0 22 22" to="360 22 22" dur="0.75s" repeatCount="indefinite"/></circle></svg></div>';
    }
    document.body.appendChild(overlay);
    overlay.style.display = '';
  }

  getSaved() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey)) || [];
    } catch {
      return [];
    }
  }

  save(term) {
    let items = this.getSaved();
    items = items.filter(t => t.toLowerCase() !== term.toLowerCase());
    items.unshift(term);

    if (items.length > this.maxItems) items.splice(this.maxItems);

    localStorage.setItem(this.storageKey, JSON.stringify(items));
    this.render();
  }

  delete(term, element) {
    let items = this.getSaved();
    items = items.filter(t => t !== term);
    localStorage.setItem(this.storageKey, JSON.stringify(items));

    element.classList.add('recent-fade-out');
    setTimeout(() => this.render(), 200);
  }

  render() {
    const items = this.getSaved();
    const show = items.length > 0;

    // 컨테이너 숨김/표시 처리
    if (this.container) {
      if (show) {
        this.container.classList.remove('is-hidden');
        this.container.style.display = "";
      } else {
        this.container.classList.add('is-hidden');
        this.container.style.display = "none";
      }
    }

    if (!this.list) return;
    this.list.innerHTML = "";

    items.forEach(term => {
      const wrapper = document.createElement('div');
      wrapper.className = 'recent-item';
      wrapper.setAttribute('data-recent-item', '');

      wrapper.addEventListener('click', (e) => {
        if (e.target.hasAttribute('data-delete-search')) return;

        // 최근 검색어 저장 (클릭 순서 업데이트)
        this.save(term);

        // 검색 URL 생성 및 바로 이동
        const searchUrl = '/search';
        let url;

        if (this.currentCollection && this.currentCollection !== 'all') {
          url = `${searchUrl}?filter.p.tag=${encodeURIComponent(this.currentCollection)}&q=${encodeURIComponent(term)}`;
        } else {
          url = `${searchUrl}?q=${encodeURIComponent(term)}`;
        }

        // dataLayer: search_keyword (recent_keyword)
        if (typeof FilaDL !== 'undefined') {
          FilaDL.push({ event: 'search_keyword', keyword: term, type: 'recent_keyword', gnb: this.getGnbLabel() });
        }

        this.showLoading();
        window.location.href = url;
      });

      const text = document.createElement('span');
      text.className = 'recent-item-text';
      text.textContent = term;

      const del = document.createElement('span');
      del.className = 'recent-item-delete';
      del.setAttribute('data-delete-search', '');
      del.textContent = '×';

      del.addEventListener('click', (e) => {
        e.stopPropagation();
        this.delete(term, wrapper);
      });

      wrapper.appendChild(text);
      wrapper.appendChild(del);
      this.list.appendChild(wrapper);
    });
  }
}

customElements.define('custom-search', CustomSearch);
